import type { ConflictFinding, ConflictLevel, FindingItem, TermChangeKind } from "../../../api/content/reviewDecoders";

/** A term an item relies on, and the term it was probably renamed to, if any. */
export interface ReliedOnTerm {
  readonly iri: string;
  readonly renamedTo: string | null;
}

/**
 * One row of the conflicts list. Findings that an item uses changed terms are merged into
 * one row per item and level, so an example that uses six changed terms is one row naming
 * the six terms, not six rows. Other findings stay one row each.
 */
export type ConflictRow =
  | {
      readonly kind: "item_relies_on";
      readonly level: ConflictLevel;
      readonly item: FindingItem;
      readonly terms: readonly ReliedOnTerm[];
      /** What changed in those terms, `other_changed` only when nothing more specific is known. */
      readonly changeKinds: readonly TermChangeKind[];
      /** References of the items the connection runs through, if not direct. */
      readonly via: readonly string[];
    }
  | { readonly kind: "single"; readonly level: ConflictLevel; readonly finding: ConflictFinding };

/** The change kinds without `other_changed` when another kind says more. */
export function specificChangeKinds(kinds: readonly TermChangeKind[]): readonly TermChangeKind[] {
  const unique = [...new Set(kinds)];
  return unique.length > 1 ? unique.filter((kind) => kind !== "other_changed") : unique;
}

/** The findings of one check as rows, in the order the check lists them. */
export function conflictRows(findings: readonly ConflictFinding[]): readonly ConflictRow[] {
  const rows: (ConflictRow | MutableItemRow)[] = [];
  const itemRows = new Map<string, MutableItemRow>();
  for (const finding of findings) {
    const item = finding.item;
    if (finding.kind !== "term_used" || item === null || finding.term === null) {
      rows.push({ kind: "single", level: finding.level, finding });
      continue;
    }
    const key = `${finding.level} ${item.itemId}`;
    let row = itemRows.get(key);
    if (row === undefined) {
      row = { kind: "item_relies_on", level: finding.level, item, terms: [], changeKinds: [], via: [] };
      itemRows.set(key, row);
      rows.push(row);
    }
    if (!row.terms.some((term) => term.iri === finding.term)) {
      row.terms.push({ iri: finding.term, renamedTo: finding.renamedTo });
    }
    row.changeKinds.push(...finding.termChangeKinds);
    if (finding.via !== null && !row.via.includes(finding.via)) {
      row.via.push(finding.via);
    }
  }
  return rows.map((row) => (row.kind === "item_relies_on" ? { ...row, changeKinds: specificChangeKinds(row.changeKinds) } : row));
}

interface MutableItemRow {
  readonly kind: "item_relies_on";
  readonly level: ConflictLevel;
  readonly item: FindingItem;
  readonly terms: ReliedOnTerm[];
  readonly changeKinds: TermChangeKind[];
  readonly via: string[];
}
